import { useEffect, useState } from 'react';
import { ExternalLink } from '@retail-ops/panel-shell/fi';
import { popoutCurrent, detectHostEnvironment } from '@/services/teamsMultiWindow';

/**
 * PopOutButton — opens the current route in a dedicated Teams floating
 * window via Stageview (stageView.open with openMode='popout').
 *
 * Shown ONLY in the Teams desktop main window (0.157.2 phone pass). Stageview
 * pop-out exists only on Teams desktop: on Teams iOS / Android the click was a
 * silent no-op ('not-desktop') that cost a top-bar slot on a 390px screen, and
 * inside a pop-out window it is meaningless. It starts hidden and appears once
 * the host check says desktop-main, so a phone never flashes it.
 * Uses the existing `shell__topbar-popout` CSS.
 */
export function PopOutButton(): JSX.Element | null {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let mounted = true;
    detectHostEnvironment()
      .then((info) => {
        if (mounted) setVisible(info.env === 'teams-desktop-main');
      })
      .catch(() => { /* default: hidden */ });
    return () => { mounted = false; };
  }, []);

  if (!visible) return null;

  const handleClick = async (): Promise<void> => {
    const result = await popoutCurrent();
    if (!result.ok && result.reason !== 'already-popout') {
      // No global toast bus in this codebase — use console.warn.
      // eslint-disable-next-line no-console
      console.warn('[popout] failed:', result.reason, result.error ?? '');
    }
  };

  return (
    <button
      type="button"
      className="shell__topbar-popout"
      onClick={handleClick}
      aria-label="Open in new window"
      title="Open in new window (Teams desktop only)"
    >
      <ExternalLink size={13} aria-hidden />
      <span className="shell__topbar-popout-label">Pop out</span>
    </button>
  );
}
